import getApiUrl from '../configuration/getApiUrl';
import getCookie from '../functions/getCookie';

export type ProviderMode = 'cloud' | 'local';
export type CloudProvider = 'openwhispr' | 'openai' | 'groq' | 'mistral' | 'custom';

export type PersistedTranscriptionNote = {
  id: string;
  title: string;
  text: string;
  fileName: string;
  providerMode: ProviderMode;
  cloudProvider?: CloudProvider;
  model: string;
  audioDurationSec?: number;
  createdAt: string;
  folder?: string;
  enhanced_content?: string;
  enhancement_prompt?: string;
};

export type PersistedNoteVersion = {
  id: string;
  label: string;
  content: string;
  prompt?: string;
  source: 'original' | 'manual' | 'ai';
  createdAt: string;
};

type NoteApiRecord = {
  id: string;
  title: string;
  text?: string;
  text_preview?: string;
  file_name: string;
  provider_mode: ProviderMode;
  cloud_provider?: CloudProvider | null;
  model: string;
  audio_duration_sec?: number | null;
  created_at: string;
  folder?: string | null;
};

type NoteListResponse = { notes: NoteApiRecord[] };
type NoteDetailResponse = { note: NoteApiRecord };

const NOTES_CACHE_KEY = 'niuniu_transcription_notes';
const NOTE_VERSION_KEY_PREFIX = 'niuniu_transcription_versions:';
const NOTE_SPLIT_RATIO_KEY = 'niuniu_note_split_ratio';
const MIN_NOTE_SPLIT_RATIO = 0.35;
const MAX_NOTE_SPLIT_RATIO = 0.75;

function toNote(apiNote: NoteApiRecord): PersistedTranscriptionNote {
  return {
    id: apiNote.id,
    title: apiNote.title,
    text: apiNote.text ?? apiNote.text_preview ?? '',
    fileName: apiNote.file_name,
    providerMode: apiNote.provider_mode,
    cloudProvider: apiNote.cloud_provider ?? undefined,
    model: apiNote.model,
    audioDurationSec: apiNote.audio_duration_sec ?? undefined,
    createdAt: apiNote.created_at,
    folder: apiNote.folder ?? undefined,
  };
}

function toApiPayload(note: PersistedTranscriptionNote) {
  return {
    id: note.id,
    title: note.title,
    text: note.text,
    file_name: note.fileName,
    provider_mode: note.providerMode,
    cloud_provider: note.cloudProvider ?? null,
    model: note.model,
    audio_duration_sec: note.audioDurationSec ?? null,
    created_at: note.createdAt,
    folder: note.folder ?? null,
  };
}

function toApiPatchPayload(updates: Partial<PersistedTranscriptionNote>) {
  const payload: Record<string, string | number | null> = {};

  if (updates.title !== undefined) payload.title = updates.title;
  if (updates.text !== undefined) payload.text = updates.text;
  if (updates.fileName !== undefined) payload.file_name = updates.fileName;
  if (updates.providerMode !== undefined) payload.provider_mode = updates.providerMode;
  if (updates.cloudProvider !== undefined) payload.cloud_provider = updates.cloudProvider ?? null;
  if (updates.model !== undefined) payload.model = updates.model;
  if (updates.audioDurationSec !== undefined) payload.audio_duration_sec = updates.audioDurationSec ?? null;
  if (updates.createdAt !== undefined) payload.created_at = updates.createdAt;
  if (updates.folder !== undefined) payload.folder = updates.folder ?? null;

  return payload;
}

export function makeTranscriptionNoteId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

export function makeNoteVersionId() {
  return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

export function loadCachedNotes(): PersistedTranscriptionNote[] {
  try {
    return JSON.parse(localStorage.getItem(NOTES_CACHE_KEY) || '[]') as PersistedTranscriptionNote[];
  } catch {
    return [];
  }
}

function saveCachedNotes(notes: PersistedTranscriptionNote[]) {
  localStorage.setItem(NOTES_CACHE_KEY, JSON.stringify(notes));
}

export function getCachedNote(noteId: string) {
  return loadCachedNotes().find(note => note.id === noteId);
}

export function upsertCachedNote(note: PersistedTranscriptionNote) {
  const notes = loadCachedNotes().filter(existing => existing.id !== note.id);
  saveCachedNotes([note, ...notes]);
}

export function removeCachedNote(noteId: string) {
  saveCachedNotes(loadCachedNotes().filter(note => note.id !== noteId));
}

function getNoteVersionKey(noteId: string) {
  return `${NOTE_VERSION_KEY_PREFIX}${noteId}`;
}

function clampNoteSplitRatio(value: number) {
  return Math.min(MAX_NOTE_SPLIT_RATIO, Math.max(MIN_NOTE_SPLIT_RATIO, value));
}

function saveNoteVersions(noteId: string, versions: PersistedNoteVersion[]) {
  localStorage.setItem(getNoteVersionKey(noteId), JSON.stringify(versions));
}

export function loadNoteVersions(noteId: string) {
  try {
    return JSON.parse(
      localStorage.getItem(getNoteVersionKey(noteId)) || '[]',
    ) as PersistedNoteVersion[];
  } catch {
    return [];
  }
}

export function ensureNoteVersions(note: PersistedTranscriptionNote) {
  const existing = loadNoteVersions(note.id);
  if (existing.length === 0) {
    const initialVersion: PersistedNoteVersion = {
      id: makeNoteVersionId(),
      label: 'V1',
      content: note.text,
      prompt: 'Original transcript',
      source: 'original',
      createdAt: note.createdAt,
    };
    saveNoteVersions(note.id, [initialVersion]);
    return [initialVersion];
  }

  if (existing.some(version => version.content === note.text)) {
    return existing;
  }

  const currentSavedVersion: PersistedNoteVersion = {
    id: makeNoteVersionId(),
    label: `V${existing.length + 1}`,
    content: note.text,
    prompt: 'Current saved note',
    source: 'manual',
    createdAt: new Date().toISOString(),
  };
  const versions = [...existing, currentSavedVersion];
  saveNoteVersions(note.id, versions);
  return versions;
}

export function appendNoteVersion(
  noteId: string,
  versionInput: Omit<PersistedNoteVersion, 'id' | 'label' | 'createdAt'> & {
    createdAt?: string;
  },
) {
  const versions = loadNoteVersions(noteId);
  const previous = versions[versions.length - 1];
  if (
    previous &&
    previous.content === versionInput.content &&
    previous.prompt === versionInput.prompt &&
    previous.source === versionInput.source
  ) {
    return { versions, activeVersion: previous };
  }

  const nextVersion: PersistedNoteVersion = {
    id: makeNoteVersionId(),
    label: `V${versions.length + 1}`,
    content: versionInput.content,
    prompt: versionInput.prompt,
    source: versionInput.source,
    createdAt: versionInput.createdAt ?? new Date().toISOString(),
  };
  const nextVersions = [...versions, nextVersion];
  saveNoteVersions(noteId, nextVersions);
  return { versions: nextVersions, activeVersion: nextVersion };
}

export function loadNoteSplitRatio(defaultRatio = 0.58) {
  try {
    const saved = Number(localStorage.getItem(NOTE_SPLIT_RATIO_KEY));
    if (Number.isFinite(saved)) {
      return clampNoteSplitRatio(saved);
    }
  } catch {
    // Ignore storage errors and use the default ratio.
  }

  return clampNoteSplitRatio(defaultRatio);
}

export function saveNoteSplitRatio(ratio: number) {
  localStorage.setItem(NOTE_SPLIT_RATIO_KEY, String(clampNoteSplitRatio(ratio)));
}

function mergeWithCached(note: PersistedTranscriptionNote) {
  const cached = getCachedNote(note.id);
  if (!cached) return note;

  const currentText = note.text || '';
  const cachedText = cached.text || '';

  return {
    ...note,
    text: cachedText.length > currentText.length ? cachedText : currentText,
    enhanced_content: cached.enhanced_content,
    enhancement_prompt: cached.enhancement_prompt,
  };
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

export async function listPersistedNotes() {
  const response = await fetch(`${getApiUrl()}/api/notes/`, {
    credentials: 'include',
  });
  const data = await parseJsonResponse<NoteListResponse>(response);
  const notes = data.notes.map(item => mergeWithCached(toNote(item)));
  notes.forEach(upsertCachedNote);
  return notes;
}

export async function getPersistedNote(noteId: string) {
  const response = await fetch(`${getApiUrl()}/api/notes/${noteId}/`, {
    credentials: 'include',
  });
  const data = await parseJsonResponse<NoteDetailResponse>(response);
  const note = mergeWithCached(toNote(data.note));
  upsertCachedNote(note);
  return note;
}

export async function savePersistedNote(note: PersistedTranscriptionNote) {
  const response = await fetch(`${getApiUrl()}/api/notes/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken') || '',
    },
    body: JSON.stringify(toApiPayload(note)),
  });
  const data = await parseJsonResponse<NoteDetailResponse>(response);
  const saved = {
    ...mergeWithCached(toNote(data.note)),
    enhanced_content: note.enhanced_content,
    enhancement_prompt: note.enhancement_prompt,
  };
  upsertCachedNote(saved);
  return saved;
}

export async function updatePersistedNote(
  noteId: string,
  updates: Partial<PersistedTranscriptionNote>,
) {
  const response = await fetch(`${getApiUrl()}/api/notes/${noteId}/`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken') || '',
    },
    body: JSON.stringify(toApiPatchPayload(updates)),
  });
  const data = await parseJsonResponse<NoteDetailResponse>(response);
  const saved = {
    ...mergeWithCached(toNote(data.note)),
    enhanced_content: updates.enhanced_content ?? getCachedNote(noteId)?.enhanced_content,
    enhancement_prompt: updates.enhancement_prompt ?? getCachedNote(noteId)?.enhancement_prompt,
  };
  upsertCachedNote(saved);
  return saved;
}

export async function deletePersistedNote(noteId: string) {
  const response = await fetch(`${getApiUrl()}/api/notes/${noteId}/`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'X-CSRFToken': getCookie('csrftoken') || '',
    },
  });

  if (!response.ok && response.status !== 404) {
    const message = await response.text();
    throw new Error(message || `Delete failed with HTTP ${response.status}`);
  }

  removeCachedNote(noteId);
}
